import React, { ReactNode } from 'react'
import { Link } from 'react-router'
import { useTranslation } from 'react-i18next'
import { useAppLocale } from '../../i18n/localeUtils'
import { Divider, Stack, Typography } from '@mui/material'
import { formatDateYYYYMMDD } from '../../packages/utils/numberFormatters'
import { FactsTable, FactsTableRow } from '../components/FactsTable'
import { EnrichedIssueEntity } from 'types/IssueEntity'
import { ConvertibleEntity } from 'types/ConvertibleEntity'
import { IssuesGroup } from 'types/IssuesGroupEntity'

export const RelatedIssues = ({ linkedIssues }: { linkedIssues: EnrichedIssueEntity['linkedIssues'] }) => {
    const { t } = useTranslation()
    const { language } = useAppLocale()

    const tableRowsIssues: ReactNode[] = []
    const tableRowsConvertibles: ReactNode[] = []

    const wrapWithLink = (
        entity: Pick<EnrichedIssueEntity, 'type' | 'id'> | Pick<ConvertibleEntity, 'id'>,
        label: ReactNode,
    ) => {
        if (!('type' in entity)) {
            return <Link to={`/convertibles/${entity.id}`}>{label}</Link>
        }
        return entity.type !== 'Guarantor fee issue' ? <Link to={`/issues/${entity.id}`}>{label}</Link> : label
    }

    const getRelatedEntities = (_linkedIssues: IssuesGroup[]) => {
        const matchedIssues = []
        const matchedConvertibles = []
        for (const group of _linkedIssues) {
            if (Array.isArray(group.issues)) {
                const issues = group.issues.filter((issue) => issue.type !== 'Guarantor fee issue')
                matchedIssues.push(...issues)
            }
            if (Array.isArray(group.convertibles)) {
                matchedConvertibles.push(...group.convertibles)
            }
        }
        return { matchedIssues, matchedConvertibles } // Return all matching issues
    }

    const { matchedIssues: relatedIssues, matchedConvertibles: relatedConvertibles } = getRelatedEntities(linkedIssues)

    const getRelatedDate = (entity: { subStartDate?: string | null; publicationDate?: string | null }) =>
        entity.subStartDate || entity.publicationDate || ''

    const sortedRelatedIssues = [...relatedIssues].sort((a, b) =>
        getRelatedDate(a).localeCompare(getRelatedDate(b)),
    )

    if (relatedIssues.length === 0 && relatedConvertibles.length === 0) {
        return null
    }

    if (sortedRelatedIssues.length > 0) {
        {
            sortedRelatedIssues.map((issue) => {
                tableRowsIssues.push(
                    <FactsTableRow
                        key={tableRowsIssues.length}
                        label={wrapWithLink(issue, t(`entities.issueType.values.${issue.type}`))}
                    >
                        {formatDateYYYYMMDD(issue.subStartDate || issue.publicationDate, { locale: language })}
                    </FactsTableRow>,
                )
            })
        }
    }

    if (relatedConvertibles.length > 0) {
        relatedConvertibles.map((convertible) => {
            tableRowsConvertibles.push(
                <FactsTableRow
                    key={tableRowsConvertibles.length}
                    label={wrapWithLink(convertible, convertible.convertibleName)}
                >
                    {formatDateYYYYMMDD(convertible.subStartDate || convertible.publicationDate, { locale: language })}
                </FactsTableRow>,
            )
        })
    }

    const showConvertibles = relatedConvertibles.length > 0

    return (
        <Stack
            sx={{
                mb: 4,
            }}
        >
            {relatedIssues.length > 0 ? (
                <>
                    <Typography variant='subtitle2'>{t('pages.issues.related.relatedIssues')}</Typography>
                    <FactsTable rows={tableRowsIssues} />
                    <Divider sx={showConvertibles ? { mb: 4 } : null} />
                </>
            ) : null}
            {showConvertibles ? (
                <>
                    <Typography variant='subtitle2'>{t('pages.issues.related.relatedConvertibles')}</Typography>
                    <FactsTable rows={tableRowsConvertibles} />
                    <Divider />
                </>
            ) : null}
        </Stack>
    )
}
